import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	Paper,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PrintIcon from '@mui/icons-material/Print';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store';
import { SectionInfoTooltip } from 'features/salesDashboard/components/SectionInfoTooltip';
import { useCashDeposit } from '../../hooks/useCashDeposit';
import { useCashBalance } from '../../hooks/useCashBalance';
import {
	confirmCashDeposit,
	annulCashDeposit,
	patchCashDeposit,
	extractApiError,
	extractDepositValidationError,
} from '../../services';
import { ICashDeposit, CASH_MOVEMENT_CATEGORY_LABELS } from '../../types';
import { VAULT_BRANCHES } from '../../constants';
import { formatCashAmount } from '../BranchCashSummary';
import { DepositStatusChip } from '../DepositActsList';

// ─── Tooltips ─────────────────────────────────────────────────────────────────

const equationInfoTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			Ecuación del depósito 🧮
		</Typography>
		<Typography variant="body2" gutterBottom>
			Total de recibos = Vueltos descontados + Caja chica retenida + Monto depositado
		</Typography>
		<Typography variant="body2">
			Cuando confirmás el acta, el sistema registra automáticamente el movimiento de retiro al banco y el ajuste de caja
			chica.
		</Typography>
	</>
);

const balanceImpactTooltip = (
	<>
		<Typography variant="body2" fontWeight={600} gutterBottom>
			¿Cómo queda la caja chica? 💰
		</Typography>
		<Typography variant="body2" gutterBottom>
			Al confirmar el acta, el <strong>resto del depósito</strong> se registra como ingreso en la caja chica.
		</Typography>
		<Typography variant="body2">
			El saldo proyectado = saldo actual de caja chica + lo que queda de este depósito.
		</Typography>
	</>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(isoString: string): string {
	return new Date(isoString).toLocaleString('es', {
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	});
}

function formatDateLong(isoString: string): string {
	return new Date(isoString).toLocaleString('es', {
		weekday: 'long',
		day: '2-digit',
		month: 'long',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
}

// ─── Cuadro de ecuación ───────────────────────────────────────────────────────

function EquationBox({ deposit }: { deposit: ICashDeposit }): JSX.Element {
	const { equation } = deposit;
	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Box display="inline-flex" alignItems="center" gap={0.5} mb={2}>
				<Typography variant="subtitle2" fontWeight={600}>
					Ecuación del depósito
				</Typography>
				<SectionInfoTooltip content={equationInfoTooltip} />
			</Box>

			<Stack spacing={1}>
				<Box display="flex" justifyContent="space-between">
					<Typography variant="body2" color="text.secondary">
						Total de recibos
					</Typography>
					<Typography variant="body2" fontWeight={600}>
						{formatCashAmount(equation.receiptsTotal, deposit.currency)}
					</Typography>
				</Box>
				{equation.changeLentTotal > 0 && (
					<Box display="flex" justifyContent="space-between">
						<Typography variant="body2" color="text.secondary">
							+ Vueltos compensados de caja chica
						</Typography>
						<Typography variant="body2" color="info.main">
							+ {formatCashAmount(equation.changeLentTotal, deposit.currency)}
						</Typography>
					</Box>
				)}
				{equation.pettyCashWithdrawn > 0 && (
					<Box display="flex" justifyContent="space-between">
						<Typography variant="body2" color="text.secondary">
							+ Retiro extra de caja chica
						</Typography>
						<Typography variant="body2">+ {formatCashAmount(equation.pettyCashWithdrawn, deposit.currency)}</Typography>
					</Box>
				)}
				<Divider />
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="body2" fontWeight={700}>
						Monto depositado en banco
					</Typography>
					<Typography variant="h6" fontWeight={700} color="primary.main">
						{formatCashAmount(equation.depositedAmount, deposit.currency)}
					</Typography>
				</Box>
				{(equation.vaultAmount ?? 0) > 0 && (
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="body2" fontWeight={700}>
							Monto que entra a caja fuerte
						</Typography>
						<Typography variant="h6" fontWeight={700} color="secondary.main">
							{formatCashAmount(equation.vaultAmount, deposit.currency)}
						</Typography>
					</Box>
				)}
				{equation.pettyCashKept > 0 && (
					<Box display="flex" justifyContent="space-between">
						<Typography variant="body2" color="text.secondary">
							Resto que queda en caja chica
						</Typography>
						<Typography variant="body2" color="warning.main" fontWeight={600}>
							{formatCashAmount(equation.pettyCashKept, deposit.currency)}
						</Typography>
					</Box>
				)}
			</Stack>
		</Paper>
	);
}

// ─── Impacto en saldo de caja chica ───────────────────────────────────────────

interface BalanceImpactProps {
	deposit: ICashDeposit;
}

function BalanceImpactBox({ deposit }: BalanceImpactProps): JSX.Element {
	const { data: balanceData, loading } = useCashBalance(deposit.branch);
	const { equation } = deposit;

	const currentBalance = balanceData?.balances.find((b) => b.currency === deposit.currency)?.balance ?? null;
	const pettyCashKept = equation.pettyCashKept ?? 0;
	const pettyCashWithdrawn = equation.pettyCashWithdrawn ?? 0;
	const projectedBalance = currentBalance !== null ? currentBalance + pettyCashKept - pettyCashWithdrawn : null;
	const isConfirmed = deposit.status === 'CONFIRMADO';

	return (
		<Paper
			variant="outlined"
			sx={{
				p: 2,
				borderColor: isConfirmed ? 'success.300' : 'warning.300',
				bgcolor: isConfirmed ? 'success.50' : 'warning.50',
			}}
		>
			<Box display="inline-flex" alignItems="center" gap={0.5} mb={1.5}>
				<AccountBalanceWalletIcon sx={{ fontSize: 18, color: isConfirmed ? 'success.main' : 'warning.main' }} />
				<Typography variant="subtitle2" fontWeight={600}>
					{isConfirmed ? 'Saldo de caja chica al confirmar' : 'Cómo queda la caja chica'}
				</Typography>
				{!isConfirmed && <SectionInfoTooltip content={balanceImpactTooltip} />}
			</Box>

			{loading ? (
				<Skeleton width={160} height={28} />
			) : (
				<Stack spacing={1}>
					{!isConfirmed && currentBalance !== null && (
						<Box display="flex" justifyContent="space-between">
							<Typography variant="body2" color="text.secondary">
								Saldo actual de caja chica ({deposit.currency})
							</Typography>
							<Typography variant="body2" fontWeight={600}>
								{formatCashAmount(currentBalance, deposit.currency)}
							</Typography>
						</Box>
					)}
					{pettyCashKept > 0 && (
						<Box display="flex" justifyContent="space-between">
							<Typography variant="body2" color="text.secondary">
								{isConfirmed ? 'Resto del depósito registrado' : '+ Resto de este depósito'}
							</Typography>
							<Typography variant="body2" fontWeight={600} color={isConfirmed ? 'success.main' : 'warning.main'}>
								{isConfirmed ? '' : '+ '}
								{formatCashAmount(pettyCashKept, deposit.currency)}
							</Typography>
						</Box>
					)}
					{pettyCashWithdrawn > 0 && (
						<Box display="flex" justifyContent="space-between">
							<Typography variant="body2" color="text.secondary">
								{isConfirmed ? 'Retiro extra registrado' : '− Retiro extra de este depósito'}
							</Typography>
							<Typography variant="body2" fontWeight={600} color="error.main">
								{isConfirmed ? '' : '− '}
								{formatCashAmount(pettyCashWithdrawn, deposit.currency)}
							</Typography>
						</Box>
					)}
					{pettyCashKept === 0 && pettyCashWithdrawn === 0 && (
						<Typography variant="body2" color="text.secondary">
							No queda resto en caja chica — se llevó exactamente el efectivo en mano.
						</Typography>
					)}
					{!isConfirmed && projectedBalance !== null && (pettyCashKept > 0 || pettyCashWithdrawn > 0) && (
						<>
							<Divider />
							<Box display="flex" justifyContent="space-between" alignItems="center">
								<Typography variant="body2" fontWeight={700}>
									Saldo proyectado ({deposit.currency})
								</Typography>
								<Typography variant="h6" fontWeight={700} color="warning.main">
									{formatCashAmount(projectedBalance, deposit.currency)}
								</Typography>
							</Box>
						</>
					)}
				</Stack>
			)}
		</Paper>
	);
}

// ─── Referencia bancaria (post-confirmación) ──────────────────────────────────

function ConfirmedBankInfo({ deposit }: { deposit: ICashDeposit }): JSX.Element | null {
	if (deposit.status !== 'CONFIRMADO') return null;

	return (
		<Paper variant="outlined" sx={{ p: 2, borderColor: 'success.400', bgcolor: 'success.50' }}>
			<Box display="flex" alignItems="center" gap={1} mb={1}>
				<CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
				<Typography variant="subtitle2" fontWeight={700} color="success.dark">
					Depósito confirmado
				</Typography>
			</Box>
			<Stack spacing={0.5}>
				{deposit.confirmedAt && (
					<Typography variant="body2" color="text.secondary">
						Confirmado el {formatDateLong(deposit.confirmedAt)}
					</Typography>
				)}
				{deposit.confirmedBy && (
					<Typography variant="body2" color="text.secondary">
						Por: <strong>{deposit.confirmedBy}</strong>
					</Typography>
				)}
				{deposit.bankReference ? (
					<Box mt={1} p={1.5} bgcolor="white" borderRadius={1} border="1px solid" borderColor="success.200">
						<Typography variant="caption" color="text.secondary" display="block">
							Referencia bancaria
						</Typography>
						<Typography variant="body1" fontWeight={700} color="success.dark">
							{deposit.bankReference}
						</Typography>
					</Box>
				) : (
					<Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
						Sin referencia bancaria registrada.
					</Alert>
				)}
				{deposit.notes && (
					<Box mt={1} p={1} bgcolor="grey.50" borderRadius={1}>
						<Typography variant="caption" color="text.secondary" display="block">
							Notas
						</Typography>
						<Typography variant="body2">{deposit.notes}</Typography>
					</Box>
				)}
			</Stack>
		</Paper>
	);
}

// ─── Edición de borrador ──────────────────────────────────────────────────────

function round2(n: number): number {
	return Math.round(n * 100) / 100;
}

interface EditDraftDialogProps {
	open: boolean;
	onClose: () => void;
	deposit: ICashDeposit;
	onSaved: () => void;
}

/**
 * Edita un acta en BORRADOR: reparto banco/caja fuerte, referencia y notas.
 * El resto a caja chica / retiro extra se recalculan solos a partir del
 * efectivo en mano (recibos + vueltos, snapshoteados en el acta). La selección
 * de vueltos no se toca acá (el API mantiene la guardada). Re-valida el API.
 */
function EditDraftDialog({ open, onClose, deposit, onSaved }: EditDraftDialogProps): JSX.Element {
	const user = useSelector((s: RootState) => s.auth.user, shallowEqual);
	const cashOnHand = round2(deposit.equation.receiptsTotal + deposit.equation.changeLentTotal);
	const showVault = VAULT_BRANCHES.includes(deposit.branch) || (deposit.vaultAmount ?? 0) > 0;

	const [amount, setAmount] = useState(String(deposit.depositedAmount));
	const [vaultStr, setVaultStr] = useState(String(deposit.vaultAmount ?? 0));
	const [bankRef, setBankRef] = useState(deposit.bankReference ?? '');
	const [notes, setNotes] = useState(deposit.notes ?? '');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const parse = (s: string): number => parseFloat(s.replace(/\./g, '').replace(',', '.')) || 0;
	const bankNum = parse(amount);
	const vaultNum = showVault ? parse(vaultStr) : 0;
	const allocated = round2(bankNum + vaultNum);
	const pettyCashKept = allocated <= cashOnHand ? round2(cashOnHand - allocated) : 0;
	const pettyCashWithdrawn = allocated > cashOnHand ? round2(allocated - cashOnHand) : 0;

	const handleSave = async () => {
		if (allocated <= 0) {
			setError('El acta tiene que llevar algo al banco o a la caja fuerte');
			return;
		}
		setLoading(true);
		setError(null);
		try {
			await patchCashDeposit(deposit.id, {
				depositedAmount: bankNum,
				vaultAmount: vaultNum,
				pettyCashKept,
				pettyCashWithdrawn,
				bankReference: bankRef.trim() || undefined,
				notes: notes.trim() || undefined,
				registeredBy: user?.email ?? 'unknown',
			});
			onSaved();
			onClose();
		} catch (err) {
			const valErr = extractDepositValidationError(err);
			setError(valErr ? valErr.message : extractApiError(err));
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
			<DialogTitle>Editar acta #{deposit.id} (borrador)</DialogTitle>
			<DialogContent>
				<DialogContentText gutterBottom>
					Podés corregir los montos mientras el acta esté en borrador — por ejemplo si el banco no aceptó todos los
					billetes. El resto a caja chica o el retiro extra se recalculan solos.
				</DialogContentText>
				<Stack spacing={2} mt={1}>
					<Box>
						<Typography variant="caption" color="text.secondary" display="block">
							Efectivo en mano del acta (recibos + vueltos)
						</Typography>
						<Typography variant="body1" fontWeight={700}>
							{formatCashAmount(cashOnHand, deposit.currency)}
						</Typography>
					</Box>
					<TextField
						label={`Monto al banco (${deposit.currency})`}
						size="small"
						fullWidth
						value={amount}
						onChange={(e) => {
							setAmount(e.target.value);
							setError(null);
						}}
					/>
					{showVault && (
						<TextField
							label={`Monto a caja fuerte (${deposit.currency})`}
							size="small"
							fullWidth
							value={vaultStr}
							onChange={(e) => {
								setVaultStr(e.target.value);
								setError(null);
							}}
							InputProps={{
								endAdornment: <LockIcon sx={{ fontSize: 15, color: 'text.secondary' }} />,
							}}
						/>
					)}
					{allocated > 0 && pettyCashKept > 0 && (
						<Alert severity="info" sx={{ py: 0.5 }}>
							Resto que queda en caja chica: <strong>{formatCashAmount(pettyCashKept, deposit.currency)}</strong>
						</Alert>
					)}
					{allocated > 0 && pettyCashWithdrawn > 0 && (
						<Alert severity="warning" sx={{ py: 0.5 }}>
							Retiro extra de caja chica: <strong>{formatCashAmount(pettyCashWithdrawn, deposit.currency)}</strong>
						</Alert>
					)}
					<TextField
						label="Referencia bancaria"
						size="small"
						fullWidth
						value={bankRef}
						onChange={(e) => setBankRef(e.target.value)}
						placeholder="Nro. de comprobante, ticket, etc."
					/>
					<TextField
						label="Notas internas"
						size="small"
						fullWidth
						multiline
						rows={2}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
					/>
					{error && <Alert severity="error">{error}</Alert>}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>
					Cancelar
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={loading || allocated <= 0}
					sx={{ textTransform: 'none' }}
				>
					{loading ? <CircularProgress size={18} /> : 'Guardar cambios'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}

// ─── Acciones: editar, confirmar y anular ─────────────────────────────────────

interface ActionsProps {
	deposit: ICashDeposit;
	onRefetch: () => void;
}

function DepositActions({ deposit, onRefetch }: ActionsProps): JSX.Element {
	const user = useSelector((s: RootState) => s.auth.user, shallowEqual);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [annulOpen, setAnnulOpen] = useState(false);
	const [bankRef, setBankRef] = useState(deposit.bankReference ?? '');
	const [annulReason, setAnnulReason] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleConfirm = async () => {
		setLoading(true);
		setError(null);
		try {
			await confirmCashDeposit(deposit.id, {
				registeredBy: user?.email ?? 'unknown',
				bankReference: bankRef.trim() || undefined,
			});
			setConfirmOpen(false);
			onRefetch();
		} catch (err) {
			setError(extractApiError(err));
		} finally {
			setLoading(false);
		}
	};

	const handleAnnul = async () => {
		if (!annulReason.trim()) return;
		setLoading(true);
		setError(null);
		try {
			await annulCashDeposit(deposit.id, {
				registeredBy: user?.email ?? 'unknown',
				reason: annulReason.trim(),
			});
			setAnnulOpen(false);
			onRefetch();
		} catch (err) {
			setError(extractApiError(err));
		} finally {
			setLoading(false);
		}
	};

	if (deposit.status === 'ANULADO') {
		return (
			<Alert severity="warning" icon={<BlockIcon />}>
				Esta acta fue anulada.{deposit.annulReason && ` Motivo: ${deposit.annulReason}`}
			</Alert>
		);
	}

	if (deposit.status === 'CONFIRMADO') return <></>;

	return (
		<>
			{error && (
				<Alert severity="error" sx={{ mb: 1 }}>
					{error}
				</Alert>
			)}
			<Box display="flex" gap={1} flexWrap="wrap" sx={{ displayPrint: 'none' }}>
				<Button
					variant="contained"
					color="success"
					startIcon={<CheckCircleIcon />}
					onClick={() => setConfirmOpen(true)}
					sx={{ textTransform: 'none' }}
				>
					Confirmar depósito
				</Button>
				<Button
					variant="outlined"
					startIcon={<EditIcon />}
					onClick={() => setEditOpen(true)}
					sx={{ textTransform: 'none' }}
				>
					Editar acta
				</Button>
				<Button
					variant="outlined"
					color="error"
					startIcon={<BlockIcon />}
					onClick={() => setAnnulOpen(true)}
					sx={{ textTransform: 'none' }}
				>
					Anular acta
				</Button>
			</Box>

			{/* Editar borrador */}
			<EditDraftDialog open={editOpen} onClose={() => setEditOpen(false)} deposit={deposit} onSaved={onRefetch} />

			{/* Confirmar */}
			<Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle>Confirmar depósito</DialogTitle>
				<DialogContent>
					<DialogContentText gutterBottom>
						Al confirmar, el sistema registra el retiro de caja chica y los recibos quedan marcados como depositados.
						Esta acción no se puede deshacer fácilmente.
					</DialogContentText>
					<TextField
						label="Referencia bancaria"
						size="small"
						fullWidth
						value={bankRef}
						onChange={(e) => setBankRef(e.target.value)}
						placeholder="Nro. de comprobante, ticket, etc."
						helperText="Completá con el número del comprobante bancario que te dieron en el banco."
						sx={{ mt: 2 }}
					/>
					{error && (
						<Alert severity="error" sx={{ mt: 1 }}>
							{error}
						</Alert>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setConfirmOpen(false)} disabled={loading} sx={{ textTransform: 'none' }}>
						Cancelar
					</Button>
					<Button
						onClick={handleConfirm}
						variant="contained"
						color="success"
						disabled={loading}
						sx={{ textTransform: 'none' }}
					>
						{loading ? <CircularProgress size={18} /> : 'Confirmar'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Anular */}
			<Dialog open={annulOpen} onClose={() => setAnnulOpen(false)} fullWidth maxWidth="xs">
				<DialogTitle>Anular acta #{deposit.id}</DialogTitle>
				<DialogContent>
					<DialogContentText gutterBottom>
						Anular genera contra-movimientos automáticos que revierten el efecto en caja chica. Los recibos quedan
						libres para un nuevo depósito.
					</DialogContentText>
					<TextField
						label="Motivo de anulación"
						size="small"
						fullWidth
						required
						value={annulReason}
						onChange={(e) => setAnnulReason(e.target.value)}
						placeholder="Describí brevemente el motivo"
						sx={{ mt: 2 }}
					/>
					{error && (
						<Alert severity="error" sx={{ mt: 1 }}>
							{error}
						</Alert>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={() => setAnnulOpen(false)} disabled={loading} sx={{ textTransform: 'none' }}>
						Cancelar
					</Button>
					<Button
						onClick={handleAnnul}
						variant="contained"
						color="error"
						disabled={loading || !annulReason.trim()}
						sx={{ textTransform: 'none' }}
					>
						{loading ? <CircularProgress size={18} /> : 'Anular'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

// ─── Componente principal ─────────────────────────────────────────────────────

interface DepositActDetailProps {
	depositId: number | string;
}

/**
 * Detalle completo de un acta de depósito: encabezado, ecuación, impacto en
 * caja chica, recibos incluidos, movimientos generados y acciones (confirmar/anular).
 *
 * Incluye botón "Imprimir PDF" que dispara window.print() con layout limpio.
 */
export function DepositActDetail({ depositId }: DepositActDetailProps): JSX.Element {
	const { deposit, loading, error, refetch } = useCashDeposit(depositId);

	if (loading) {
		return (
			<Stack spacing={2}>
				<Skeleton variant="rectangular" height={100} />
				<Skeleton variant="rectangular" height={200} />
			</Stack>
		);
	}

	if (error) {
		return <Alert severity="error">{error}</Alert>;
	}

	if (!deposit) return <></>;

	return (
		<>
			{/* Estilos solo para impresión */}
			<style>{`
				@media print {
					.no-print { display: none !important; }
					body { background: white !important; }
					header, nav, aside { display: none !important; }
					.MuiDrawer-root, .MuiAppBar-root { display: none !important; }
					.print-page { max-width: 100% !important; padding: 0 !important; }
				}
			`}</style>

			<Stack spacing={3} className="print-page">
				{/* ── Header ─────────────────────────────────── */}
				<Paper variant="outlined" sx={{ p: 2 }}>
					<Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1} mb={1}>
						<Box>
							<Typography variant="h6" fontWeight={700}>
								Acta de depósito #{deposit.id}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{deposit.branch} · {deposit.currency} · Creada el {formatDateTime(deposit.createdAt)}
							</Typography>
						</Box>
						<Box display="flex" alignItems="center" gap={1}>
							{(deposit.vaultAmount ?? 0) > 0 && (
								<Chip
									icon={<LockIcon sx={{ fontSize: 14 }} />}
									label={
										deposit.depositedAmount > 0
											? `Caja fuerte: ${formatCashAmount(deposit.vaultAmount, deposit.currency)}`
											: 'Caja fuerte'
									}
									size="small"
									color="secondary"
									variant="outlined"
								/>
							)}
							<DepositStatusChip status={deposit.status} />
							{/* Botón imprimir — solo visible en pantalla */}
							<Tooltip title="Generá el PDF desde el diálogo de impresión del navegador">
								<Button
									size="small"
									variant="outlined"
									startIcon={<PrintIcon />}
									onClick={() => window.print()}
									sx={{ textTransform: 'none', displayPrint: 'none' }}
								>
									Imprimir PDF
								</Button>
							</Tooltip>
						</Box>
					</Box>

					<Box display="flex" gap={2} flexWrap="wrap">
						<Typography variant="caption" color="text.secondary">
							Creado por: {deposit.registeredBy}
						</Typography>
						{deposit.annulledAt && (
							<Typography variant="caption" color="error.main">
								Anulada: {formatDateTime(deposit.annulledAt)}
							</Typography>
						)}
					</Box>

					{/* Notas (si están en borrador se muestran acá; si está confirmado, se muestran en ConfirmedBankInfo) */}
					{deposit.notes && deposit.status !== 'CONFIRMADO' && (
						<Box mt={1} p={1} bgcolor="grey.50" borderRadius={1}>
							<Typography variant="caption" color="text.secondary">
								{deposit.notes}
							</Typography>
						</Box>
					)}
				</Paper>

				{/* ── Referencia bancaria (post-confirmación) ─ */}
				<ConfirmedBankInfo deposit={deposit} />

				{/* ── Ecuación ───────────────────────────────── */}
				<EquationBox deposit={deposit} />

				{/* ── Impacto en caja chica ──────────────────── */}
				<BalanceImpactBox deposit={deposit} />

				{/* ── Acciones ───────────────────────────────── */}
				<DepositActions deposit={deposit} onRefetch={refetch} />

				{/* ── Recibos incluidos ──────────────────────── */}
				{deposit.receipts && deposit.receipts.length > 0 && (
					<Box>
						<Typography variant="subtitle2" fontWeight={600} gutterBottom>
							Recibos incluidos ({deposit.receipts.length})
						</Typography>
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Recibo</TableCell>
										<TableCell>Orden</TableCell>
										<TableCell align="right">Monto</TableCell>
										<TableCell>Conciliado</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{deposit.receipts.map((r) => (
										<TableRow key={r.receiptId} hover>
											<TableCell>{r.receiptNumber}</TableCell>
											<TableCell>
												{r.saleOrderId ? (
													<RouterLink to={`/salesorder/${r.saleOrderId}`} style={{ color: 'inherit' }}>
														{r.saleOrderId}
													</RouterLink>
												) : (
													<Typography variant="body2" color="text.disabled">
														—
													</Typography>
												)}
											</TableCell>
											<TableCell align="right">{formatCashAmount(r.amount, r.currency)}</TableCell>
											<TableCell>
												{r.reconciled ? (
													<Chip
														label="Conciliado"
														size="small"
														color="success"
														variant="outlined"
														sx={{ fontSize: 11 }}
													/>
												) : (
													<Chip
														label="Pendiente"
														size="small"
														color="default"
														variant="outlined"
														sx={{ fontSize: 11 }}
													/>
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}

				{/* ── Movimientos generados ──────────────────── */}
				{deposit.movements && deposit.movements.length > 0 && (
					<Box>
						<Typography variant="subtitle2" fontWeight={600} gutterBottom>
							Movimientos de caja generados
						</Typography>
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Fecha</TableCell>
										<TableCell>Categoría</TableCell>
										<TableCell align="right">Monto</TableCell>
										<TableCell>Nota</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{deposit.movements.map((mov) => (
										<TableRow key={mov.id}>
											<TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(mov.createdAt)}</TableCell>
											<TableCell>
												{CASH_MOVEMENT_CATEGORY_LABELS[mov.category] ?? mov.category}
												{mov.box === 'FUERTE' && (
													<Chip label="Caja fuerte" size="small" variant="outlined" sx={{ fontSize: 10, ml: 0.5 }} />
												)}
											</TableCell>
											<TableCell align="right">
												<Typography
													variant="body2"
													fontWeight={600}
													color={mov.type === 'EGRESO' ? 'error.main' : 'success.main'}
												>
													{mov.type === 'EGRESO' ? '−' : '+'}
													{formatCashAmount(mov.amount, mov.currency)}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{mov.note ?? '—'}
												</Typography>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}

				{/* Firma para el impreso */}
				<Box sx={{ display: 'none', displayPrint: 'block', mt: 4 }}>
					<Divider sx={{ mb: 3 }} />
					<Box display="flex" justifyContent="space-between">
						<Box textAlign="center" sx={{ minWidth: 180 }}>
							<Divider sx={{ mb: 0.5 }} />
							<Typography variant="caption">Firma TL / Responsable de caja</Typography>
						</Box>
						<Box textAlign="center" sx={{ minWidth: 180 }}>
							<Divider sx={{ mb: 0.5 }} />
							<Typography variant="caption">Firma Administración</Typography>
						</Box>
					</Box>
				</Box>
			</Stack>
		</>
	);
}
